import React from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  Box,
} from '@mui/material';
import { Language as LanguageIcon } from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';

interface LanguageToggleProps {
  variant?: 'icon' | 'menu' | 'select';
  showLabel?: boolean;
}

export const LanguageToggle: React.FC<LanguageToggleProps> = ({ 
  variant = 'icon',
  showLabel = false 
}) => {
  const { language, setLanguage, availableLanguages, t } = useLanguage();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (langCode: string) => {
    setLanguage(langCode);
    handleClose();
  };

  const currentLanguage = availableLanguages.find(lang => lang.code === language);

  if (variant === 'menu') {
    return (
      <MenuItem onClick={handleClick}>
        <ListItemIcon>
          <LanguageIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>
          {t('common.language')}
        </ListItemText>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          {currentLanguage?.nativeName}
        </Typography>
      </MenuItem>
    );
  }

  return (
    <>
      <Tooltip title={t('common.language')}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={handleClick}
            color="inherit"
            aria-label="change language"
            aria-controls="language-menu"
            aria-haspopup="true"
          >
            <LanguageIcon />
          </IconButton>
          {showLabel && (
            <Typography variant="body2" sx={{ ml: 1 }}>
              {currentLanguage?.code.toUpperCase()}
            </Typography>
          )}
        </Box>
      </Tooltip>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {availableLanguages.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            selected={language === lang.code}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Typography sx={{ mr: 2, minWidth: '30px' }}>
                {lang.code.toUpperCase()}
              </Typography>
              <Typography>
                {lang.nativeName}
              </Typography>
              {language === lang.code && (
                <Typography variant="caption" color="primary" sx={{ ml: 'auto' }}>
                  ✓
                </Typography>
              )}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// Compact version for use in limited space
export const LanguageToggleCompact: React.FC = () => {
  const { language, setLanguage, availableLanguages } = useLanguage();

  const toggleLanguage = () => {
    const currentIndex = availableLanguages.findIndex(lang => lang.code === language);
    const nextIndex = (currentIndex + 1) % availableLanguages.length;
    setLanguage(availableLanguages[nextIndex].code);
  };

  return (
    <Tooltip title="Switch language">
      <IconButton onClick={toggleLanguage} size="small" color="inherit">
        <Typography variant="button" sx={{ fontWeight: 'bold' }}>
          {language.toUpperCase()}
        </Typography>
      </IconButton>
    </Tooltip>
  );
};